import React from 'react'

import { Spinner } from '@acorns/web-components'
import { datadogRum } from '@datadog/browser-rum'
import gravatarUrl from 'gravatar-url'
import { compose, lifecycle, withState } from 'recompose'
import styled from 'styled-components'

import { FadeIn } from 'components/FadeIn'
import { style } from 'theme'
import { useCheatCode } from 'utils/key/hacks/useCheatCode'
import { USER_AVATAR } from 'utils/testing/testIDs'

export enum Size {
  XS = 28,
  SM = 32,
  MD = 70,
  LG = 90,
  XL = 120,
}

type Props = {
  className?: string
  email?: string
  size?: Size
  disabled?: boolean
}

type StateProps = {
  imgUrl: string
  loaded: boolean
  setLoaded: (loaded: boolean) => void
  setImgUrl: (imgUrl: string) => void
}

type InnerProps = Props & StateProps

const enhance = compose<InnerProps, Props>(
  withState('imgUrl', 'setImgUrl', ''),
  withState('loaded', 'setLoaded', false),
  lifecycle({
    componentDidMount() {
      const { email, imgUrl, size, setImgUrl, setLoaded } = this
        .props as InnerProps

      if (!imgUrl) {
        const profileImg = gravatarUrl(email || 'fake@notreal.com', {
          default: 'mm',
          size,
          rating: 'pg',
        })

        // The Gravatar API simply returns a URL string. It doesn't actually download the image.
        // We want to display the loading spinner until the actual picture has been fully downloaded.
        fetch(profileImg)
          .then((response) => response.blob())
          .then((blob) => {
            setImgUrl(URL.createObjectURL(blob))
            setLoaded(true)
          })
          .catch((err) => {
            datadogRum.addError(Error(`Unable to fetch profile image: ${err}`))
          })
      }
    },
  }),
)

const Img = styled.input`
  border: 3px solid white;
  border-radius: 50%;

  &:focus {
    border: 2px solid ${style((theme) => theme.colors.green)};
    margin: -2px;
  }
`

const UserImage: React.FC<InnerProps> = ({
  className,
  imgUrl,
  loaded,
  disabled,
}) => {
  const { handleKeyDown, secretImg } = useCheatCode()

  return (
    <FadeIn>
      {loaded ? (
        <Img
          className={className}
          src={secretImg ?? imgUrl}
          alt="user profile"
          data-testid={USER_AVATAR}
          onKeyDown={handleKeyDown}
          type="image"
          disabled={disabled}
        />
      ) : (
        <Spinner height={35} width={35} />
      )}
    </FadeIn>
  )
}

export default enhance(UserImage)
export { enhance, UserImage }
